import { createSlice } from "@reduxjs/toolkit";

/**
 * AFMS inspections redux slice. Post-Plan M cleanup:
 *
 * The platform-core L3 components (`src/inspections/pages/Types` etc.)
 * own checklist mutation + record-detail flows directly against
 * inspections-api. The slice keeps only the buckets a surviving AFMS
 * page actually selects:
 *
 *   - `checklistListing`     → InspectionScheduleForm's picker dropdown.
 *   - `scheduleListing` + `scheduleDetails` + `createSchedule` + `updateSchedule`
 *                            → InspectionList + InspectionScheduleForm.
 *   - `overdueSchedules` + `upcomingSchedules`
 *                            → InspectionList header chip + ComplianceDashboard.
 *   - `recordListing`        → InspectionList records tab.
 *   - `updateRecord`         → shared write target for start / complete /
 *                              cancel / reschedule on records.
 *
 * Buckets retired in this cleanup (M5.4 follow-up):
 *   - checklistDetails, createChecklist, updateChecklist
 *   - recordDetails, createRecord
 * along with the matching `*Start/Success/Failure/Reset` reducers + exports.
 */

const initialState = {
  // Checklist Templates
  checklistListing: {
    loading: false,
    error: null,
    data: null,
  },
  // Inspection Schedules
  scheduleListing: {
    loading: false,
    error: null,
    data: null,
  },
  scheduleDetails: {
    loading: false,
    error: null,
    data: null,
  },
  overdueSchedules: {
    loading: false,
    error: null,
    data: null,
  },
  upcomingSchedules: {
    loading: false,
    error: null,
    data: null,
  },
  createSchedule: {
    loading: false,
    error: null,
    data: null,
  },
  updateSchedule: {
    loading: false,
    error: null,
    data: null,
  },
  // Inspection Records
  recordListing: {
    loading: false,
    error: null,
    data: null,
  },
  updateRecord: {
    loading: false,
    error: null,
    data: null,
  },
};

const inspectionsSlice = createSlice({
  name: "inspections",
  initialState,
  reducers: {
    // Checklist Listing
    checklistListingStart: (state) => {
      state.checklistListing.loading = true;
      state.checklistListing.error = null;
    },
    checklistListingSuccess: (state, action) => {
      state.checklistListing.loading = false;
      state.checklistListing.data = action.payload;
      state.checklistListing.error = null;
    },
    checklistListingFailure: (state, action) => {
      state.checklistListing.loading = false;
      state.checklistListing.error = action.payload;
    },
    checklistListingReset: (state) => {
      state.checklistListing = initialState.checklistListing;
    },

    // Schedule Listing
    scheduleListingStart: (state) => {
      state.scheduleListing.loading = true;
      state.scheduleListing.error = null;
    },
    scheduleListingSuccess: (state, action) => {
      state.scheduleListing.loading = false;
      state.scheduleListing.data = action.payload;
      state.scheduleListing.error = null;
    },
    scheduleListingFailure: (state, action) => {
      state.scheduleListing.loading = false;
      state.scheduleListing.error = action.payload;
    },
    scheduleListingReset: (state) => {
      state.scheduleListing = initialState.scheduleListing;
    },

    // Schedule Details
    scheduleDetailsStart: (state) => {
      state.scheduleDetails.loading = true;
      state.scheduleDetails.error = null;
    },
    scheduleDetailsSuccess: (state, action) => {
      state.scheduleDetails.loading = false;
      state.scheduleDetails.data = action.payload;
      state.scheduleDetails.error = null;
    },
    scheduleDetailsFailure: (state, action) => {
      state.scheduleDetails.loading = false;
      state.scheduleDetails.error = action.payload;
    },
    scheduleDetailsReset: (state) => {
      state.scheduleDetails = initialState.scheduleDetails;
    },

    // Overdue Schedules
    overdueSchedulesStart: (state) => {
      state.overdueSchedules.loading = true;
      state.overdueSchedules.error = null;
    },
    overdueSchedulesSuccess: (state, action) => {
      state.overdueSchedules.loading = false;
      state.overdueSchedules.data = action.payload;
      state.overdueSchedules.error = null;
    },
    overdueSchedulesFailure: (state, action) => {
      state.overdueSchedules.loading = false;
      state.overdueSchedules.error = action.payload;
    },

    // Upcoming Schedules
    upcomingSchedulesStart: (state) => {
      state.upcomingSchedules.loading = true;
      state.upcomingSchedules.error = null;
    },
    upcomingSchedulesSuccess: (state, action) => {
      state.upcomingSchedules.loading = false;
      state.upcomingSchedules.data = action.payload;
      state.upcomingSchedules.error = null;
    },
    upcomingSchedulesFailure: (state, action) => {
      state.upcomingSchedules.loading = false;
      state.upcomingSchedules.error = action.payload;
    },

    // Create Schedule
    createScheduleStart: (state) => {
      state.createSchedule.loading = true;
      state.createSchedule.error = null;
      state.createSchedule.data = null;
    },
    createScheduleSuccess: (state, action) => {
      state.createSchedule.loading = false;
      state.createSchedule.data = action.payload;
      state.createSchedule.error = null;
    },
    createScheduleFailure: (state, action) => {
      state.createSchedule.loading = false;
      state.createSchedule.error = action.payload;
    },
    createScheduleReset: (state) => {
      state.createSchedule = initialState.createSchedule;
    },

    // Update Schedule
    updateScheduleStart: (state) => {
      state.updateSchedule.loading = true;
      state.updateSchedule.error = null;
      state.updateSchedule.data = null;
    },
    updateScheduleSuccess: (state, action) => {
      state.updateSchedule.loading = false;
      state.updateSchedule.data = action.payload;
      state.updateSchedule.error = null;
    },
    updateScheduleFailure: (state, action) => {
      state.updateSchedule.loading = false;
      state.updateSchedule.error = action.payload;
    },
    updateScheduleReset: (state) => {
      state.updateSchedule = initialState.updateSchedule;
    },

    // Record Listing
    recordListingStart: (state) => {
      state.recordListing.loading = true;
      state.recordListing.error = null;
    },
    recordListingSuccess: (state, action) => {
      state.recordListing.loading = false;
      state.recordListing.data = action.payload;
      state.recordListing.error = null;
    },
    recordListingFailure: (state, action) => {
      state.recordListing.loading = false;
      state.recordListing.error = action.payload;
    },
    recordListingReset: (state) => {
      state.recordListing = initialState.recordListing;
    },

    // Update Record — shared dispatch target for start / complete /
    // cancel / reschedule (every record-mutating thunk reuses this bucket).
    updateRecordStart: (state) => {
      state.updateRecord.loading = true;
      state.updateRecord.error = null;
      state.updateRecord.data = null;
    },
    updateRecordSuccess: (state, action) => {
      state.updateRecord.loading = false;
      state.updateRecord.data = action.payload;
      state.updateRecord.error = null;
    },
    updateRecordFailure: (state, action) => {
      state.updateRecord.loading = false;
      state.updateRecord.error = action.payload;
    },
    updateRecordReset: (state) => {
      state.updateRecord = initialState.updateRecord;
    },
  },
});

export const {
  checklistListingStart,
  checklistListingSuccess,
  checklistListingFailure,
  checklistListingReset,
  scheduleListingStart,
  scheduleListingSuccess,
  scheduleListingFailure,
  scheduleListingReset,
  scheduleDetailsStart,
  scheduleDetailsSuccess,
  scheduleDetailsFailure,
  scheduleDetailsReset,
  overdueSchedulesStart,
  overdueSchedulesSuccess,
  overdueSchedulesFailure,
  upcomingSchedulesStart,
  upcomingSchedulesSuccess,
  upcomingSchedulesFailure,
  createScheduleStart,
  createScheduleSuccess,
  createScheduleFailure,
  createScheduleReset,
  updateScheduleStart,
  updateScheduleSuccess,
  updateScheduleFailure,
  updateScheduleReset,
  recordListingStart,
  recordListingSuccess,
  recordListingFailure,
  recordListingReset,
  updateRecordStart,
  updateRecordSuccess,
  updateRecordFailure,
  updateRecordReset,
} = inspectionsSlice.actions;

export default inspectionsSlice.reducer;
